import React, { useState } from 'react';
import { Card, Typography, Button, Space, Alert, Row, Col, Tag } from 'antd';
import { BookOutlined, ApiOutlined, CodeOutlined, LinkOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const APIDocumentation: React.FC = () => {
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://web-production-bdd98.up.railway.app/api';
  const apiDocsUrl = `${apiBaseUrl.replace('/api', '')}/api-docs`;
  const apiDocsJsonUrl = `${apiBaseUrl.replace('/api', '')}/api-docs.json`;

  const codeExamples = {
    login: `// JavaScript/TypeScript
const response = await fetch('${apiBaseUrl}/station-owners/login', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    email: 'user@example.com',
    password: 'yourpassword'
  })
});

const data = await response.json();
const token = data.token; // JWT token'ı saklayın`,

    authenticatedRequest: `// Authenticated Request
const response = await fetch('${apiBaseUrl}/station-owners/stations', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer YOUR_JWT_TOKEN'
  }
});

const stations = await response.json();`,

    axios: `// Axios ile kullanım
import axios from 'axios';

const api = axios.create({
  baseURL: '${apiBaseUrl}',
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor - token ekle
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('stationOwnerToken');
  if (token) {
    config.headers.Authorization = \`Bearer \${token}\`;
  }
  return config;
});

// Kullanım
const stations = await api.get('/station-owners/stations');`,

    curl: `# cURL örneği
# Login
curl -X POST ${apiBaseUrl}/station-owners/login \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "yourpassword"
  }'

# Authenticated Request
curl -X GET ${apiBaseUrl}/station-owners/stations \\
  -H "Authorization: Bearer YOUR_JWT_TOKEN"`,

    python: `# Python örneği
import requests

# Login
response = requests.post('${apiBaseUrl}/station-owners/login', json={
    'email': 'user@example.com',
    'password': 'yourpassword'
})

token = response.json()['token']

# Authenticated Request
headers = {'Authorization': f'Bearer {token}'}
stations = requests.get('${apiBaseUrl}/station-owners/stations', headers=headers).json()`
  };

  const [selectedExample, setSelectedExample] = useState('login');

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '24px' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        <ApiOutlined /> API Dokümantasyonu
      </Title>

      <Alert
        message="Swagger UI Dokümantasyonu"
        description={
          <div>
            <p>Detaylı API dokümantasyonu için Swagger UI'yi kullanabilirsiniz:</p>
            <Space>
              <Button
                type="primary"
                icon={<LinkOutlined />}
                href={apiDocsUrl}
                target="_blank"
              >
                Swagger UI'yi Aç
              </Button>
              <Button
                icon={<ApiOutlined />}
                href={apiDocsJsonUrl}
                target="_blank"
              >
                OpenAPI JSON İndir
              </Button>
            </Space>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title={<><BookOutlined /> API Bilgileri</>}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Base URL:</Text>
                <br />
                <Text code>{apiBaseUrl}</Text>
              </div>
              <div>
                <Text strong>Authentication:</Text>
                <br />
                <Text>JWT Bearer Token kullanılır</Text>
                <br />
                <Text type="secondary">
                  Header: <code>Authorization: Bearer YOUR_TOKEN</code>
                </Text>
              </div>
              <div>
                <Text strong>Rate Limiting:</Text>
                <br />
                <Text>API çağrıları rate limit ile korunmaktadır</Text>
              </div>
              <div>
                <Text strong>Response Format:</Text>
                <br />
                <Text>Tüm yanıtlar JSON formatındadır</Text>
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<><CodeOutlined /> Hızlı Başlangıç</>}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>1. Giriş Yapın</Text>
                <br />
                <Text type="secondary">POST /station-owners/login</Text>
              </div>
              <div>
                <Text strong>2. Token'ı Alın</Text>
                <br />
                <Text type="secondary">Response'dan token'ı saklayın</Text>
              </div>
              <div>
                <Text strong>3. API'yi Kullanın</Text>
                <br />
                <Text type="secondary">Her istekte Authorization header'ına token ekleyin</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Kod Örnekleri" style={{ marginTop: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Button
              type={selectedExample === 'login' ? 'primary' : 'default'}
              onClick={() => setSelectedExample('login')}
            >
              Login
            </Button>
            <Button
              type={selectedExample === 'authenticatedRequest' ? 'primary' : 'default'}
              onClick={() => setSelectedExample('authenticatedRequest')}
            >
              Authenticated Request
            </Button>
            <Button
              type={selectedExample === 'axios' ? 'primary' : 'default'}
              onClick={() => setSelectedExample('axios')}
            >
              Axios
            </Button>
            <Button
              type={selectedExample === 'curl' ? 'primary' : 'default'}
              onClick={() => setSelectedExample('curl')}
            >
              cURL
            </Button>
            <Button
              type={selectedExample === 'python' ? 'primary' : 'default'}
              onClick={() => setSelectedExample('python')}
            >
              Python
            </Button>
          </Space>

          <Card
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: 'monospace',
              marginTop: '16px'
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {codeExamples[selectedExample as keyof typeof codeExamples]}
            </pre>
          </Card>
        </Space>
      </Card>

      <Card title="Ana Endpoint'ler" style={{ marginTop: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Tag color="blue">POST</Tag>
            <Text strong> /station-owners/login</Text>
            <br />
            <Text type="secondary">İstasyon sahibi girişi</Text>
          </div>
          <div>
            <Tag color="green">GET</Tag>
            <Text strong> /station-owners/stations</Text>
            <br />
            <Text type="secondary">İstasyonları listele</Text>
          </div>
          <div>
            <Tag color="orange">POST</Tag>
            <Text strong> /station-owners/stations</Text>
            <br />
            <Text type="secondary">Yeni istasyon ekle</Text>
          </div>
          <div>
            <Tag color="green">GET</Tag>
            <Text strong> /station-owners/campaigns</Text>
            <br />
            <Text type="secondary">Kampanyaları listele</Text>
          </div>
          <div>
            <Tag color="green">GET</Tag>
            <Text strong> /station-owners/competition/regional-comparison</Text>
            <br />
            <Text type="secondary">Bölgesel fiyat karşılaştırması</Text>
          </div>
          <div>
            <Tag color="green">GET</Tag>
            <Text strong> /station-owners/competition/market-analysis</Text>
            <br />
            <Text type="secondary">Pazar analizi</Text>
          </div>
        </Space>
      </Card>

      <Card title="Hata Kodları" style={{ marginTop: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Tag color="red">400</Tag>
            <Text>Bad Request - Geçersiz istek</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Tag color="red">401</Tag>
            <Text>Unauthorized - Kimlik doğrulama gerekli</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Tag color="red">403</Tag>
            <Text>Forbidden - Yetki yok</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Tag color="red">404</Tag>
            <Text>Not Found - Kaynak bulunamadı</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Tag color="red">500</Tag>
            <Text>Internal Server Error - Sunucu hatası</Text>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default APIDocumentation;

